import React, { useEffect, useRef, useState } from 'react';
import { ProgressLoader } from '../../Elements';
import ConfirmationModal from '../../ConfirmationModal';
import MaterailTable from '../../Elements/MaterialTable';
import HtmlTooltip from '../../HtmlTooltilp';
import { Typography } from '@mui/material';
import { customerExcelSchema } from '../../../../common/validators/customerExcelSchema';

interface ViewExcelTableProps {
    data: any[] | null;
    loading: boolean;
    disableSave: (value: boolean) => void;
}

const ViewExcelTable: React.FC<ViewExcelTableProps> = ({ data, loading, disableSave }) => {
    const confirmModalRef = useRef<any>();
    const viewExcelTableRef = useRef<any>();
    const [errorCounter, setErrorCounter] = useState<number>(0);

    // local error counter
    const localErrorCounter = useRef<number>(0);

    // use a Set to track errors
    const errorMap = useRef<Set<string>>(new Set());

    useEffect(() => {
        disableSave(errorCounter > 0);
    }, [errorCounter]);

    useEffect(() => {
        handleErrorCounter();
    }, [data]);

    const handleErrorCounter = () => {
        if (!data || data.length === 0) {
            setErrorCounter(0);
            errorMap.current.clear();
            localErrorCounter.current = 0;
        }

        setErrorCounter(localErrorCounter.current);
    }

    const LinearColor = () => (loading ? <ProgressLoader /> : null);

    const validateData = (value: any, id: any) => {
        try {
            // validate data
            customerExcelSchema.validateSyncAt(id, { [id]: value });
            return <div>{value}</div>;
        } catch (error: any) {

            localErrorCounter.current += 1;

            return (
                <HtmlTooltip
                    title={
                        <React.Fragment>
                            <Typography color="inherit">Hatalı İçerik</Typography>
                            <br />
                            <label className="font-bold">Hata Mesajı: </label> {error.errors[0]}
                        </React.Fragment>
                    }
                >
                    <div className="bg-error-10 p-1 rounded-md text-xs text-error-dark text-center inline-block">
                        {value}
                    </div>
                </HtmlTooltip>
            );
        }
    };

    const headCells = [
        {
            id: 'row',
            numeric: false,
            label: 'Sıra',
            className: "min-w-[50px]",
            maxLength: 30,
            colWidth: 30,
            forcealign: "center"
        },
        {
            id: 'customercode',
            numeric: false,
            label: 'Müşteri Kodu',
            colWidth: 20,
            className: "min-w-[150px]",
            maxLength: 15,
            forcealign: "center",
            isRender: true,
            renderRow: (row: any, rowIndex: number) => (
                <>
                    {validateData(row.customercode, "customercode")}
                </>
            )
        },
        {
            id: 'customername',
            numeric: false,
            label: 'Müşteri Adı',
            className: "min-w-[360px]",
            maxLength: 30,
            colWidth: 30,
            isRender: true,
            renderRow: (row: any, rowIndex: number) => (
                <>
                    {validateData(row.customername, "customername")}
                </>
            )
        },
        {
            id: 'taxcode',
            numeric: false,
            label: 'Vergi Numarası',
            className: "min-w-[150px]",
            maxLength: 30,
            colWidth: 30,
            forcealign: "center",
            isRender: true,
            renderRow: (row: any, rowIndex: number) => (
                <>
                    {validateData(row.taxcode, "taxcode")}
                </>
            )
        },
        {
            id: 'taxoffice',
            numeric: false,
            label: 'Vergi Dairesi',
            className: "min-w-[130px]",
            maxLength: 30,
            colWidth: 30,
            isRender: true,
            renderRow: (row: any, rowIndex: number) => (
                <>
                    {validateData(row.taxoffice, "taxoffice")}
                </>
            )
        },
        {
            id: 'country',
            numeric: false,
            label: 'Ülke',
            className: "min-w-[100px]",
            maxLength: 30,
            colWidth: 30,
        },
        {
            id: 'city',
            numeric: false,
            label: 'Şehir',
            className: "min-w-[100px]",
            maxLength: 30,
            colWidth: 30,
        },
        {
            id: 'district',
            numeric: false,
            label: 'İlçe',
            className: "min-w-[100px]",
            maxLength: 30,
            colWidth: 30,
        },
        {
            id: 'postalcode',
            numeric: false,
            label: 'Posta Kodu',
            className: "min-w-[130px]",
            maxLength: 30,
            colWidth: 30,
            forcealign: "center"
        },
        {
            id: 'address',
            numeric: false,
            disablePadding: true,
            label: 'Adres',
            isprimary: true,
            className: "min-w-[150px]",
            colWidth: 10,
            hide: true
        },
        {
            id: 'mobile',
            numeric: false,
            label: 'Telefon',
            className: "min-w-[110px]",
            maxLength: 30,
            colWidth: 30,
            forcealign: "center",
        },
        {
            id: 'phone',
            numeric: false,
            label: 'Cep Tel.',
            className: "min-w-[110px]",
            maxLength: 15,
            colWidth: 20,
            forcealign: "center",
            isRender: true,
            renderRow: (row: any, rowIndex: number) => (
                <>
                    {validateData(row.phone, "phone")}
                </>
            )
        },
        {
            id: 'email',
            numeric: false,
            label: 'Email',
            className: "min-w-[110px]",
            maxLength: 15,
            colWidth: 20,
            isRender: true,
            renderRow: (row: any, rowIndex: number) => (
                <>
                    {validateData(row.email, "email")}
                </>
            )
        },
        {
            id: 'tccode',
            numeric: false,
            label: 'TC Kimlik No',
            className: "min-w-[150px]",
            maxLength: 30,
            colWidth: 30,
            forcealign: "center",
            isRender: true,
            renderRow: (row: any, rowIndex: number) => (
                <>
                    {validateData(row.tccode, "tccode")}
                </>
            )
        },
    ];

    return (
        <>
            {errorCounter > 0 ? <div className='text-danger pl-5'>Kayıtlardaki Hata Sayısı: {errorCounter}</div> : ""}
            {LinearColor()}
            <ConfirmationModal ref={confirmModalRef} />
            <MaterailTable
                ref={viewExcelTableRef}
                headerCells={headCells}
                tableData={data && data.length > 0 ? data : ""}
                emptyMessage='Müşterileri görüntülemek için bir excel dosyası yükleyin.'
                hideToolTip
            />
        </>
    )
}

export default ViewExcelTable